// MessageSequenceEditor — US-129.
//
// Renders the 4 message slots: an optional invitation note, two mandatory
// follow-ups and one optional follow-up. Placeholder syntax is normalized at
// the editing boundary so drafts cannot persist conflicting token grammars.

import type { ChangeEvent } from 'react';

import type { CampaignStepKind } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Button, Card, Input, Pill, Textarea } from '@barel/ui';

import {
  CAMPAIGN_PLACEHOLDERS,
  canonicalizeCampaignPlaceholders,
  renderCampaignMessagePreview,
  type CampaignMessageSequenceValue,
  type CampaignMessageStepValue,
} from './lib/sequence';

export type MessageStepValue = CampaignMessageStepValue;
export type MessageSequenceValue = CampaignMessageSequenceValue;

export interface MessageSequenceEditorProps {
  value: MessageSequenceValue;
  onChange: (next: MessageSequenceValue) => void;
}

interface SlotProps {
  kind: CampaignStepKind;
  icon: string;
  label: string;
  day: string;
  optional?: boolean;
  hint?: string;
  step: MessageStepValue;
  onChange: (next: MessageStepValue) => void;
}

function MessageSlot({ kind, icon, label, day, optional, hint, step, onChange }: SlotProps) {
  const t = useT('campaigns');
  const dimmed = !step.enabled;

  return (
    <Card
      padding="md"
      radius="xl"
      data-testid={`message-slot-${kind}`}
      style={{ opacity: dimmed ? 0.55 : 1 }}
    >
      <div className="flex items-center gap-3">
        <span
          aria-hidden="true"
          className="flex h-8 w-8 items-center justify-center rounded-full text-base"
          style={{ background: 'var(--bg-elevated)' }}
        >
          {icon}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <strong className="text-primary text-sm">{label}</strong>
            <Pill tone="neutral" size="xs">
              {day}
            </Pill>
            {optional ? (
              <span className="text-tertiary text-xs italic">
                {t('create.sectionMessages.optional')}
              </span>
            ) : null}
          </div>
        </div>

        {kind !== 'invite' && step.enabled ? (
          <div className="flex items-center gap-2">
            <span className="text-tertiary text-xs">{t('create.sectionMessages.after')}</span>
            <Input
              type="number"
              min={1}
              max={60}
              value={step.delayDays}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const parsed = parseInt(e.target.value, 10);
                onChange({
                  ...step,
                  delayDays: Number.isFinite(parsed) && parsed >= 1 ? Math.min(60, parsed) : 1,
                });
              }}
              size="sm"
              style={{ width: 72 }}
              data-testid={`message-slot-${kind}-days`}
            />
            <span className="text-tertiary text-xs">{t('create.sectionMessages.days')}</span>
          </div>
        ) : null}

        {optional ? (
          <Button
            size="sm"
            variant={step.enabled ? 'success' : 'ghost'}
            onClick={() => onChange({ ...step, enabled: !step.enabled })}
            data-testid={`message-slot-${kind}-toggle`}
          >
            {step.enabled
              ? t('create.sectionMessages.enabled')
              : t('create.sectionMessages.enable')}
          </Button>
        ) : null}
      </div>

      {step.enabled ? (
        <div className="mt-3 flex flex-col gap-2">
          <label className="text-tertiary text-xs font-semibold">
            {t('create.sectionMessages.messageLabel')}
          </label>
          <Textarea
            value={step.messageText}
            onChange={(e) =>
              onChange({
                ...step,
                messageText: canonicalizeCampaignPlaceholders(e.target.value).slice(0, 300),
              })
            }
            rows={3}
            maxLength={300}
            placeholder={
              t('create.sectionMessages.placeholder', {
                firstName: CAMPAIGN_PLACEHOLDERS.firstName,
                company: CAMPAIGN_PLACEHOLDERS.company,
              }) as string
            }
            data-testid={`message-slot-${kind}-text`}
          />
          <div className="flex flex-wrap items-center justify-between gap-2 text-tertiary text-xs">
            <span>
              {t('create.sectionMessages.placeholders', {
                firstName: CAMPAIGN_PLACEHOLDERS.firstName,
                company: CAMPAIGN_PLACEHOLDERS.company,
              })}
            </span>
            <span>{t('create.sectionMessages.counter', { count: step.messageText.length })}</span>
          </div>
          {step.messageText.trim() ? (
            <div
              className="rounded-lg bg-bg-elevated px-3 py-2 text-secondary text-xs"
              data-testid={`message-slot-${kind}-preview`}
            >
              <strong>{t('create.sectionMessages.previewLabel')}</strong>{' '}
              {renderCampaignMessagePreview(step.messageText, {
                firstName: t('create.sectionMessages.previewFirstName') as string,
                company: t('create.sectionMessages.previewCompany') as string,
              })}
            </div>
          ) : null}
          {hint ? <div className="text-tertiary text-xs italic">{hint}</div> : null}
        </div>
      ) : null}
    </Card>
  );
}

export function MessageSequenceEditor({ value, onChange }: MessageSequenceEditorProps) {
  const t = useT('campaigns');

  const updateInvit = (next: MessageStepValue) => onChange({ ...value, invit: next });
  const updateFu1 = (next: MessageStepValue) => onChange({ ...value, fu1: next });
  const updateFu2 = (next: MessageStepValue) => onChange({ ...value, fu2: next });
  const updateFu3 = (next: MessageStepValue) => onChange({ ...value, fu3: next });

  return (
    <div className="flex flex-col gap-3" data-testid="message-sequence-editor">
      <MessageSlot
        kind="invite"
        icon="👋"
        label={t('create.sectionMessages.invitLabel') as string}
        day={t('create.sectionMessages.invitDay') as string}
        hint={t('create.sectionMessages.invitHint') as string}
        step={value.invit}
        onChange={updateInvit}
      />
      <MessageSlot
        kind="fu1"
        icon="💬"
        label={t('create.sectionMessages.fu1Label') as string}
        day={`J+${value.fu1.delayDays}`}
        step={value.fu1}
        onChange={updateFu1}
      />
      <MessageSlot
        kind="fu2"
        icon="💡"
        label={t('create.sectionMessages.fu2Label') as string}
        day={`J+${value.fu2.delayDays}`}
        step={value.fu2}
        onChange={updateFu2}
      />
      <MessageSlot
        kind="fu3"
        icon="🎯"
        label={t('create.sectionMessages.fu3Label') as string}
        day={`J+${value.fu3.delayDays}`}
        optional
        step={value.fu3}
        onChange={updateFu3}
      />
    </div>
  );
}
